import React, { ReactNode, useState } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Session } from "ezStore";

import { Checkbox, FormGroup, Input, Select, Textarea } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import ProductLoadingTypeSelect from "components/selects/ProductLoadingTypeSelect";

import { DataFormat, Fields, hasOptionalFieldPermission, Language } from "utils";

import ProductCategorySelect from "view/productcategories/components/selects/ProductCategorySelect";
import { ProductVolumeCorrectionConversionMethod } from "view/products/components/form/fields/ProductVolumeCorrectionConversionMethod";
import { ProductSchemaInput } from "view/products/utils/product.schema";

const ProductGeneral = ({
	control,
	watch,
	setValue,
	session,
}: {
	control: Control<ProductSchemaInput>,
	watch: UseFormWatch<ProductSchemaInput>,
	setValue: UseFormSetValue<ProductSchemaInput>,
	session: Session
}) => {

	const [ isDangerousGood, density, densityMin, densityMax, volumeCorrectionConversionMethod ] = watch([ "isDangerousGood", "densityKgM3", "densityKgM3Min", "densityKgM3Max", "volumeCorrectionConversionMethod" ]);

	return (
		<div>
			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_LOADINGTYPE")}>
				<Controller
					name={"loadingType"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<ProductLoadingTypeSelect {...field} value={field.value || undefined} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement='left-center' label={Language("PRODUCTS_DANGEROUS_GOOD")}>
				<Controller
					name={"isDangerousGood"}
					control={control}
					render={({ field }) => <Checkbox checked={field.value} onChange={event => field.onChange(event.target.checked)} />} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_NAME")}>
				<Controller
					name={"name"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_PREVIOUS_CARGO_DISPLAY_NAME")}>
				<Controller
					name={"previousCargoDisplayName"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_CODE")}>
				<Controller
					name={"code"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "abbreviation") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("ABBREVIATION")}>
					<Controller
						name={"abbreviation"}
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.PRODUCT, "category") && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCT_CATEGORY")}>
					<Controller
						name={"category"}
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<ProductCategorySelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_CAS_NUMBER")}>
				<Controller
					name={"casNumber"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_DESCRIPTION")}>
				<Controller
					name={"description"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{!isDangerousGood && (
				<>
					<FormGroup
						overflow="visible"
						labelPlacement="left"
						label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_EN")}
						labelPaddingTop={12}
						required>
						<Controller
							name={"properShippingNameEN"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup
						overflow="visible"
						labelPlacement="left"
						label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_NL")}
						labelPaddingTop={12}
						required>
						<Controller
							name={"properShippingNameNL"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup
						overflow="visible"
						labelPlacement="left"
						label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_DE")}
						labelPaddingTop={12}
						required>
						<Controller
							name={"properShippingNameDE"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup
						overflow="visible"
						labelPlacement="left"
						label={Language("PRODUCT_ADN_PROPER_SHIPPING_NAME_FR")}
						labelPaddingTop={12}
						required>
						<Controller
							name={"properShippingNameFR"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				</>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("NOTES")}>
				<Controller
					name={"remark"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Textarea {...field} rows={4} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{!isDangerousGood && (
				<>
					<RangedInput
						control={control}
						name="densityKgM3"
						nameMin="densityKgM3Min"
						nameMax="densityKgM3Max"
						setValue={setValue}
						label={Language("PRODUCT_ADN_DENSITY")}
						addonAfter={DataFormat.UNITS.DENSITY}
						watch={watch}
						step=".0001"
					/>

					<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCT_ADN_DENSITY_TEMPERATURE")}>
						<Controller
							name={"densityTemperature"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input
										disabled={!(density || densityMin || densityMax)}
										{...field}
										value={field.value as string}
										addonAfter={"°C"}
										type="number"
										step=".01" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				</>
			)}

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VOLUME_CORRECTION_CONVERSION_METHOD")}>
				<Controller
					name={"volumeCorrectionConversionMethod"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<ProductVolumeCorrectionConversionMethod {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{volumeCorrectionConversionMethod === "DENSITY_CORRECTION_FACTOR" && (
				<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_DENSITY_CORRECTION_FACTOR")}>
					<Controller
						name={"densityCorrectionFactor"}
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input
									{...field}
									value={field.value as string}
									type="number"
									step=".0001" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<RangedInput
				name="vaporPressure"
				nameMin="vaporPressureMin"
				nameMax="vaporPressureMax"
				addonAfter="kPa"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_VAPOR_PRESSURE")}
				watch={watch} />

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_VAPOR_PRESSURE_TEMPERATURE")}>
				<Controller
					name={"vaporPressureTemperature"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								addonAfter={"°C"}
								type="number"
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<RangedInput
				name="viscosity"
				nameMin="viscosityMin"
				nameMax="viscosityMax"
				addonAfter="mm²/s"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_VISCOSITY")}
				watch={watch} />

			<RangedInput
				addonAfter={"°C"}
				name="flashPoint"
				nameMin="flashPointMin"
				nameMax="flashPointMax"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_FLASH_POINT")}
				watch={watch} />

			<RangedInput
				addonAfter={"°C"}
				name="meltingPoint"
				nameMin="meltingPointMin"
				nameMax="meltingPointMax"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_MELTING_POINT")}
				watch={watch} />

			<RangedInput
				addonAfter={"°C"}
				name="boilingPoint"
				nameMin="boilingPointMin"
				nameMax="boilingPointMax"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_BOILING_POINT")}
				watch={watch} />

			<RangedInput
				addonAfter={"°C"}
				name="autoIgnitionTemperature"
				nameMin="autoIgnitionTemperatureMin"
				nameMax="autoIgnitionTemperatureMax"
				control={control}
				setValue={setValue}
				label={Language("PRODUCTS_AUTO_IGNITION_TEMPERATURE")}
				watch={watch} />

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_SELF_ACCELERATING_DECOMPOSITION_POLYMERISATION_TEMPERATURE")}>
				<Controller
					name={"selfAcceleratingDecompositionPolymerisationTemperature"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								type="number"
								addonAfter={"°C"}
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_MAXIMUM_CONTACT_TEMPERATURE")}>
				<Controller
					name={"maximumContactTemperature"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								type="number"
								addonAfter={"°C"}
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_UPPER_EXPLOSIVE_LIMIT")}>
				<Controller
					name={"upperExplosiveLimit"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								type="number"
								addonAfter={"%"}
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_LOWER_EXPLOSIVE_LIMIT")}>
				<Controller
					name={"lowerExplosiveLimit"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								type="number"
								addonAfter={"%"}
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_ACCEPTED_VENT_FREE_LEVEL")}>
				<Controller
					name={"acceptedVentFreeLevel"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input
								{...field}
								value={field.value as string}
								type="number"
								addonAfter={"%"}
								step=".01" />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_PRODUCT_INSTRUCTIONS_FOR_VESSEL")}>
				<Controller
					name={"productInstructionsForVessel"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Textarea {...field} rows={4} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement="left-center" label={Language("PRODUCTS_HEATING_INSTRUCTIONS_FOR_VESSEL")}>
				<Controller
					name={"heatingInstructionsForVessel"}
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Textarea {...field} rows={4} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement='left-center' label={Language("PRODUCTS_IS_ACTIVE")}>
				<Controller
					name={"isActive"}
					control={control}
					render={({ field }) => <Checkbox checked={field.value} onChange={event => field.onChange(event.target.checked)} />} />
			</FormGroup>

			<FormGroup overflow="visible" labelPlacement='left-center' label={Language("PRODUCTS_IS_VALIDATED")}>
				<Controller
					name={"isValidated"}
					control={control}
					render={({ field }) => <Checkbox checked={field.value} onChange={event => field.onChange(event.target.checked)} />}  />
			</FormGroup>
		</div>
	);
};

enum RangedInputType {
	ABSOLUTE = "Absolute",
	RANGED = "Ranged"
}

export const RangedInput = ({
	control,
	name,
	nameMin,
	nameMax,
	label,
	addonAfter,
	setValue,
	labelWidth,
	watch,
	step = ".01",
}: {
	control: Control<ProductSchemaInput>,
	name: keyof ProductSchemaInput,
	nameMin: keyof ProductSchemaInput,
	nameMax: keyof ProductSchemaInput,
	label: ReactNode,
	addonAfter?: string,
	setValue: UseFormSetValue<ProductSchemaInput>,
	labelWidth?: number;
	watch: UseFormWatch<ProductSchemaInput>,
	step?: string
}) => {
	const [ minValue, maxValue ] = watch([ nameMin, nameMax ]);
	const [ type, setType ] = useState<RangedInputType>((minValue || maxValue) ? RangedInputType.RANGED : RangedInputType.ABSOLUTE);

	return (
		<>
			<FormGroup
				overflow="visible"
				labelPlacement="left"
				label={label}
				labelPaddingTop={12}
				labelWidth={labelWidth}>

				<Grid container spacing={3}>
					<Grid size={{ xs: 4 }}>
						<Select
							disableSearch
							clearable={false}
							value={type}
							onChange={(val) => {
								if (val === RangedInputType.ABSOLUTE) {
									setValue(nameMin, undefined);
									setValue(nameMax, undefined);
								} else if (val === RangedInputType.RANGED) {
									setValue(name, undefined);
								}

								setType(val as RangedInputType);
							}}
							fetchOptions={Object.values(RangedInputType)} />
					</Grid>

					{type === RangedInputType.ABSOLUTE ? (
						<Grid size={{ xs: 8 }}>
							<Controller
								control={control}
								render={({ field: { value, onChange, name }, formState }) => (
									<React.Fragment>
										<Input
											value={value ? value as number : undefined}
											onChange={onChange}
											addonAfter={addonAfter}
											type="number"
											step={step} />
										<FormErrorMessage name={name} errors={formState.errors} />
									</React.Fragment>
								)}
								name={name} />
						</Grid>
					) : (
						<React.Fragment>
							<Grid size={{ xs: 4 }}>
								<Controller
									control={control}
									render={({ field: { value, onChange, name }, formState }) => (
										<React.Fragment>
											<Input
												addonAfter={addonAfter}
												value={value as keyof ProductSchemaInput}
												onChange={onChange}
												type="number"
												addonBefore={Language("MIN")}
												step={step} />
											<FormErrorMessage name={name} errors={formState.errors} />
										</React.Fragment>)}
									name={nameMin as keyof ProductSchemaInput} />
							</Grid>

							<Grid size={{ xs: 4 }}>
								<Controller
									control={control}
									render={({ field: { value, onChange, name }, formState }) => (
										<React.Fragment>
											<Input
												addonAfter={addonAfter}
												value={value as keyof ProductSchemaInput}
												onChange={onChange}
												type="number"
												addonBefore={Language("MAX")}
												step={step} />
											<FormErrorMessage name={name} errors={formState.errors} />
										</React.Fragment>
									)}
									name={nameMax as keyof ProductSchemaInput} />
							</Grid>
						</React.Fragment>
					)}
				</Grid>
			</FormGroup>
		</>
	);
};

export default ProductGeneral;
